import React, {useMemo, useState} from "react";
import {useRef} from "react";
import {useNavigation} from "@react-navigation/native";
import {Text,Animated, TouchableOpacity,StyleSheet, View} from "react-native";
import WebPageLayout from "../../../../layouts/WebPageLayout";
import TablePageLayout from "../../../../layouts/TablePageLayout";
import {CoreTable} from "../../../../components/CoreTable";
import {DataTable, Button} from "react-native-paper";
import {Button as AntButton, Tooltip,Modal, Checkbox} from "antd";
import {Feather, MaterialIcons} from "@expo/vector-icons";
import {AlignCenterOutlined, ExportOutlined, FilePdfOutlined, PlusOutlined} from "@ant-design/icons";
import {BaseFilters} from "../../../../../web/hooks/useFilters";
import useEntityListPage from "../../../../hooks/useEntityListPage";
import {
  useLazyExportLabourListQuery,
  useLazyGetLabourListQuery,
  useSetWorkOrderStatusMutation
} from "../../../../../../store/services/api/work-order-api";
import {
  WorkOrder,
  WorkOrderPriority,
  WorkOrderStatus,
  WorkOrderType
} from "../../../../../../store/data/entities/work-order";
import {LabourListDto} from "../../../../../../store/data/entities/work-order-labour";
import LabourListScreenFilters from "./LabourListScreenFilters";
import {
  humanizeDate,
  humanizeEnum,
  humanizePastDate,
  humanizeTime,
  humanizeWorkOrderPriority
} from "../../../../../../utils/humanizers";
import useHasPermission from "../../../../../../hooks/useHasPermission";
import {useExcelExport} from "../../../../hooks/useExport";
import {Basket, BasketIcon} from "../../../../components/Basket";
import useBasket, {BasketItem} from "../../../../../../hooks/useBasket";
import { DateTime } from "luxon";
import ColumnExportModal, { ExportableColumn } from "../../../../../../components/modals/ColumnExportModal";



interface LabourListScreenFilters extends BaseFilters {
  reference?: string;
  description?: string;

  priority?: WorkOrderPriority[];
  startDate?: string;
  endDate?: string;
  statuses?: WorkOrderStatus[];
  type?: WorkOrderType;
  buildingLeadId?: string[];
  technicianId?: string[];
}

export default function LabourList() {
  const navigation = useNavigation<any>();


  const {exportExcel, isLoading: exportLoading} = useExcelExport({
    query: useLazyExportLabourListQuery
  });


  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [selectedColumns, setSelectedColumns] = useState<string[]>([]);

  const exportableColumns: ExportableColumn[] = [
    { key: 'ReferenceNumber', label: 'Reference Number'},
    { key: 'Description', label: 'Description' },
    { key: 'FormattedDate', label: 'Date' },
    { key: 'FormattedStartDateTime', label: 'Start Time'},
    { key: 'FormattedEndDateTime', label: 'End Time' },
    { key: 'User', label: 'User'},
    { key: 'FormattedHoursWorked', label: 'Hours Worked' },
  ];

 


  const {
    onSubmitFilter,
    filter,
    tableData,
    isLoading
  } = useEntityListPage<LabourListScreenFilters>({
    entityName: "Labour",
    lazyTableQuery: useLazyGetLabourListQuery,
    filterDefinition: {
      sortedColumn: 'workOrder.referenceNumber',
      sortedColumnAscending: false,
      pageNumber: 1,
      pageSize: 50,
      statuses: [
        WorkOrderStatus.PendingApproval,
        WorkOrderStatus.Approved,
        WorkOrderStatus.InProgress,
        WorkOrderStatus.Completed
      ]
    }
  });



  const extendedData = useMemo(() => {
  if (!tableData) return tableData;

  const totalHours = (tableData.list ?? []).reduce(
    (sum, row) => sum + (row.hoursWorked ?? 0),
    0
  );

  return {
    ...tableData,
    list: [
      ...tableData.list,
      {
        id: "total-row",
        isSummary: true,
        hoursWorked: totalHours
      } as LabourListDto & { isSummary: boolean }
    ]
  };
}, [tableData]);
   const [filtersVisible, setFiltersVisible] = useState(true);
    const filtersWidth = useRef(new Animated.Value(filtersVisible ? 1 : 0)).current;
  
    const toggleFilters = () => {
      const toValue = filtersVisible ? 0 : 1;
      Animated.timing(filtersWidth, {
        toValue,
        duration: 300,
        useNativeDriver: false,
      }).start();
      setFiltersVisible(!filtersVisible);
    };

  return (
    <WebPageLayout
      header={{
        previousTab: {title: 'Dashboard', routeName: 'dashboard', replaceStack: true},
        tabs: [
          {title: 'Labour', routeName: 'list'}
        ]
      }}
    >
      <TablePageLayout
        filters={
          <Animated.View
            style={{
              overflow: "hidden",
              width: filtersWidth.interpolate({
                inputRange: [0, 1],
                outputRange: [0, 300], 
              }),
              height: "100%",
            }}
          >
            <LabourListScreenFilters filter={filter} onSubmit={onSubmitFilter} />
          </Animated.View>
        }
        actions={(
          <>
            <Button
              icon={filtersVisible ? "chevron-left" : "chevron-right"}
              mode="contained-tonal"
              onPress={toggleFilters}
              style={{ marginRight: 8 }}
            >
              {filtersVisible ? "Hide Filters" : "Show Filters"}
            </Button>            
            <AntButton
              size='large'
              icon={<ExportOutlined />}
              onClick={() => setExportModalOpen(true)}
            >
              Export Excel
            </AntButton>
          </>
        )}
      >
        <CoreTable<LabourListDto & { isSummary?: boolean }>
  isLoading={isLoading ||  exportLoading}
  headers={[
    { text: 'Reference', sortedName: 'workOrder.referenceNumber',flex: 0.7},
    { text: 'Description', sortedName: 'workOrder.description', flex: 1.5},
    { text: 'Date', sortedName: 'date' ,flex: 0.6},
    { text: 'Start Time', sortedName: 'startDateTime',flex: 0.6 },
    { text: 'End Time', sortedName: 'endDateTime' ,flex: 0.6},
    { text: 'User', sortedName: 'user', flex: 0.7  },
    { text: 'Hours', sortedName: 'hoursWorked' },
  ]}
  showCheckbox={true}
  data={extendedData}
  keyExtractor={(item) => item.id}
  renderItem={({ item, index, headers }) => {
  const isSummary = item.isSummary;

  return (
    <CoreTable.Row key={isSummary ? "total-row" : index}>
      {headers.map((header, i) => {
        switch (header.text) {

          case 'Reference':
            return (
              <CoreTable.Cell key={i} style={{ flex: header.flex, ...styles.header }}>
                {isSummary ? <Text style={styles.totalText}>Total Hours</Text> : (item.referenceNumber ?? 'N/A')}
              </CoreTable.Cell>
            );
          
          case 'Description':
            return (
              <CoreTable.Cell key={i} style={{ flex: header.flex, ...styles.header }}>
                {isSummary ? "" : (item.description ?? 'N/A')}
              </CoreTable.Cell>
            );

          case 'User':
          case 'Date':
          case 'Start Time':
          case 'End Time':
            return (
              <CoreTable.Cell key={i} style={{ flex: header.flex, ...styles.header }}>
                {isSummary ? "" : (
                  header.text === 'User' ? item.user ?? 'N/A' :
                  header.text === 'Date' ? humanizeDate(item?.date) :
                  header.text === 'Start Time' ? humanizeTime(item?.startDateTime) :
                  humanizeTime(item?.endDateTime)
                )}
              </CoreTable.Cell>
            );

          case 'Hours':
            return (
              <CoreTable.Cell key={i} style={{ flex: header.flex, ...styles.header }}>
                {isSummary ? (
                  <Text style={styles.totalText}>{item.hoursWorked}</Text>
                ) : (
                  item.hoursWorked
                )}
              </CoreTable.Cell>
            );

          default:
            return <CoreTable.Cell key={i} style={{ flex: header.flex, ...styles.header }} />;
        }
      })}
    </CoreTable.Row>
  );
}}

  filters={filter.filters}
  setFilter={filter.setFilter}
/>   
        <ColumnExportModal
          open={exportModalOpen}
          onClose={() => setExportModalOpen(false)}
          onConfirm={(cols) => {
            exportExcel({
              ...filter.getFilterValues(),
              columns: cols,
            });
            setExportModalOpen(false);
          }}
          columns={exportableColumns}
          selectedColumns={selectedColumns}
          setSelectedColumns={setSelectedColumns}
        />

      </TablePageLayout>
    </WebPageLayout>
  );
}
const styles = StyleSheet.create({
  header: {
    flexDirection: "row",
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: "#e0e0e0",
    alignItems: "flex-start",
    paddingHorizontal: 0
  },
  totalText: {
  fontWeight: "bold",
  fontSize: 16
}
});